import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { X, Smartphone, Share, Plus } from "lucide-react";

/** Install-to-home-screen prompt for Fortune OS.
 * - Android / Chromium: hooks the `beforeinstallprompt` event and shows our own banner.
 * - iOS Safari: shows a one-time tutorial card explaining the Share → Add to Home Screen flow.
 * Only shown to authenticated users on /portal/* routes (and only once per device).
 */
const STORAGE_KEY = "fortune-pwa-prompt-dismissed";
const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !window.MSStream;

export default function PWAInstallPrompt() {
  const { user } = useAuth();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosTip, setIosTip] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (isStandalone()) return;
    if (typeof window === "undefined") return;
    if (!window.location.pathname.startsWith("/portal")) return;
    if (localStorage.getItem(STORAGE_KEY)) return;

    if (isIOS()) {
      // iOS does not support beforeinstallprompt, show the tutorial card after a small delay
      const t = setTimeout(() => setIosTip(true), 2500);
      return () => clearTimeout(t);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [user]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    setShow(false);
    setIosTip(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === "accepted") dismiss();
    else setShow(false);
  };

  if (show && deferred) {
    return (
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] w-[92vw] max-w-md bg-[#12283F] text-white border border-[#B8965A] shadow-2xl"
           data-testid="pwa-install-banner">
        <div className="flex items-start gap-3 p-4">
          <div className="w-10 h-10 bg-[#B8965A] flex items-center justify-center text-[#12283F]">
            <Smartphone size={20}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="eyebrow text-[#B8965A] text-[9px] mb-0.5">Install Fortune OS</div>
            <p className="text-sm leading-snug">Add to your home screen for one-tap access to your sourcing dashboard, ledger, and trip schedule.</p>
            <div className="flex gap-2 mt-3">
              <button data-testid="pwa-install-btn" onClick={install}
                className="flex-1 bg-[#B8965A] text-[#12283F] text-[11px] uppercase tracking-[0.18em] px-3 py-2 font-semibold">
                Install
              </button>
              <button data-testid="pwa-dismiss-btn" onClick={dismiss}
                className="text-white/65 hover:text-white text-[11px] uppercase tracking-[0.18em] px-3">
                Not now
              </button>
            </div>
          </div>
          <button onClick={dismiss} aria-label="Close" className="text-white/50 hover:text-white"><X size={16}/></button>
        </div>
      </div>
    );
  }

  if (iosTip) {
    return (
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] w-[92vw] max-w-md bg-[#12283F] text-white border border-[#B8965A] shadow-2xl"
           data-testid="pwa-ios-tip">
        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="eyebrow text-[#B8965A] text-[9px]">Install Fortune OS on iPhone</div>
            <button onClick={dismiss} aria-label="Close" className="text-white/50 hover:text-white"><X size={16}/></button>
          </div>
          <p className="text-sm leading-snug mt-2">Tap the share icon in Safari, then choose <strong>Add to Home Screen</strong>.</p>
          <div className="flex items-center gap-3 mt-3 text-[11px] text-white/80">
            <span className="flex items-center gap-1"><Share size={14} className="text-[#B8965A]"/> Share</span>
            <span className="text-white/40">→</span>
            <span className="flex items-center gap-1"><Plus size={14} className="text-[#B8965A]"/> Add to Home Screen</span>
          </div>
          <button data-testid="pwa-ios-dismiss" onClick={dismiss}
            className="mt-3 w-full bg-[#B8965A] text-[#12283F] text-[11px] uppercase tracking-[0.18em] px-3 py-2 font-semibold">
            Got it
          </button>
        </div>
      </div>
    );
  }

  return null;
}
